class HeroSlider {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('.hero-slider__track');
    this.slides = Array.from(root.querySelectorAll('.hero-slider__slide'));
    this.dots = Array.from(root.querySelectorAll('[data-hs-dot]'));
    this.prevBtn = root.querySelector('[data-hs-prev]');
    this.nextBtn = root.querySelector('[data-hs-next]');
    this.categoriesTrack = root.querySelector('[data-hs-categories-track]');
    this.transition = root.dataset.transition || 'fade';
    this.autoplaySpeed = (parseFloat(root.dataset.autoplay) || 5) * 1000;
    this.autoplayEnabled = root.dataset.autoplayEnabled !== 'false';
    this.current = 0;
    this.timer = null;

    this.defaultCategoriesHTML = this.categoriesTrack ? this.categoriesTrack.innerHTML : '';
    // Tracks which content is currently rendered in categoriesTrack: `undefined`
    // means the default categories (the server-rendered initial state, so the
    // first updateCategories(0) call below correctly does nothing when slide 0
    // has no override), or a slide's raw override string. Lets updateCategories
    // skip the innerHTML rewrite when the target slide's categories are already
    // showing - see that method for why this matters (most transitions are
    // default-to-default and would otherwise rebuild identical DOM every time).
    this.categoriesOverrideKey = undefined;

    if (this.slides.length > 1) {
      this.bindEvents();
      this.bindSwipe();
      this.bindViewportObserver();
      this.startAutoplay();
    }

    this.updateCategories(0);
    this.bindCategoryModals();
  }

  bindCategoryModals() {
    const modal = this.root.querySelector('[data-hs-cat-modal]');
    if (!modal) return;
    const overlay = modal.querySelector('[data-hs-cat-modal-overlay]');
    const closeBtn = modal.querySelector('[data-hs-cat-modal-close]');
    const body = modal.querySelector('[data-hs-cat-modal-body]');
    const titleEl = modal.querySelector('[data-hs-cat-modal-title]');
    let lastFocused = null;

    const openModal = (trigger) => {
      const targetId = trigger.getAttribute('data-modal-target');
      const tpl = this.root.querySelector('template[data-cat-modal-id="' + targetId + '"]');
      if (!tpl || !body) return;
      body.innerHTML = '';
      body.appendChild(tpl.content.cloneNode(true));
      if (titleEl) titleEl.textContent = trigger.getAttribute('data-modal-title') || '';
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => modal.classList.add('is-open'));
      if (closeBtn) closeBtn.focus();
    };

    const closeModal = () => {
      if (modal.hidden) return;
      modal.classList.remove('is-open');
      document.body.style.overflow = '';
      window.setTimeout(() => { modal.hidden = true; }, 250);
      if (lastFocused && lastFocused.focus) lastFocused.focus();
    };

    // Delegated: category tiles get rebuilt via innerHTML on every slide
    // change (see updateCategories), so listeners bound directly to a tile
    // would be lost - binding on the stable root survives that replacement.
    this.root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-hs-cat-modal-trigger]');
      if (trigger) {
        e.preventDefault();
        openModal(trigger);
      }
    });

    if (overlay) overlay.addEventListener('click', closeModal);
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    // Carousel arrows for horizontal modal sections. Delegated on `body`
    // (which persists across opens) since its content is replaced wholesale
    // by openModal() every time, so directly-bound listeners never survive.
    if (body) {
      body.addEventListener('click', (e) => {
        const arrow = e.target.closest('[data-hs-modal-arrow]');
        if (!arrow) return;
        const wrap = arrow.closest('.hero-slider__modal-grid-wrap');
        const grid = wrap ? wrap.querySelector('[data-hs-modal-grid]') : null;
        if (!grid) return;
        const rtl = getComputedStyle(grid).direction === 'rtl';
        const dir = arrow.getAttribute('data-hs-modal-arrow') === 'next' ? 1 : -1;
        const sign = rtl ? -dir : dir;
        grid.scrollBy({ left: sign * grid.clientWidth * 0.9, behavior: 'smooth' });
      });
    }
  }

  // Mirrors slideshow.js's SlideshowViewportObserver + [in-viewport] pattern:
  // autoplay should not do per-tick DOM work (goTo() rewrites classList on
  // every slide/dot) while this section is scrolled off-screen. A single
  // per-instance observer is enough here (unlike slideshow.js's shared
  // singleton), since a page has at most a couple of hero-slider instances.
  bindViewportObserver() {
    if (!('IntersectionObserver' in window)) {
      // No IntersectionObserver support: fall back to always-visible, matching
      // this file's prior behavior (autoplay gated by document.hidden only).
      this.root.setAttribute('in-viewport', '');
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.root.setAttribute('in-viewport', '');
          } else {
            this.root.removeAttribute('in-viewport');
          }
        }
      },
      { rootMargin: '100px' }
    );
    observer.observe(this.root);
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.goTo(this.current - 1);
        this.restartAutoplay();
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.goTo(this.current + 1);
        this.restartAutoplay();
      });
    }
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.goto, 10);
        this.goTo(index);
        this.restartAutoplay();
      });
    });

    this.root.addEventListener('mouseenter', () => this.stopAutoplay());
    this.root.addEventListener('mouseleave', () => this.startAutoplay());
  }

  bindSwipe() {
    // Touch/mouse swipe works independently of the arrow buttons, so it stays
    // the primary navigation method on mobile even when arrows are hidden.
    if (!this.track) return;
    const threshold = 50;
    let dragging = false;
    let startX = 0;
    let deltaX = 0;

    this.track.addEventListener('pointerdown', (e) => {
      if (e.target.closest('a, button')) return;
      dragging = true;
      startX = e.clientX;
      deltaX = 0;
      this.stopAutoplay();
      if (this.track.setPointerCapture) this.track.setPointerCapture(e.pointerId);
    });

    this.track.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      deltaX = e.clientX - startX;
    });

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      if (Math.abs(deltaX) > threshold) {
        this.goTo(this.current + (deltaX < 0 ? 1 : -1));
      }
      deltaX = 0;
      this.restartAutoplay();
    };

    this.track.addEventListener('pointerup', endDrag);
    this.track.addEventListener('pointercancel', endDrag);
    this.track.addEventListener('pointerleave', () => { if (dragging) endDrag(); });
  }

  goTo(index) {
    const total = this.slides.length;
    const newIndex = (index + total) % total;

    this.slides.forEach((slide, i) => {
      slide.classList.remove('is-active', 'is-prev');
      if (i === newIndex) {
        slide.classList.add('is-active');
      } else if (i === this.current && this.transition === 'slide') {
        slide.classList.add('is-prev');
      }
    });

    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === newIndex);
    });

    this.current = newIndex;
    this.updateCategories(newIndex);
  }

  updateCategories(index) {
    if (!this.categoriesTrack) return;
    const slide = this.slides[index];
    if (!slide) return;

    const overrideRaw = slide.dataset.categoriesOverride;

    // Perf: skip the innerHTML rewrite entirely when the target slide's
    // categories content is identical to what's already rendered - on this
    // store, 19 of 20 hero slides have no override, so autoplay (every 3s,
    // indefinitely, with no pause on touch devices since they never fire
    // hover) was rebuilding the exact same DOM on ~95% of ticks for zero
    // visual change. `overrideRaw` is `undefined` for every non-override
    // slide, so this same check also correctly catches override-to-default
    // and default-to-override transitions (the values differ), not just
    // identical-override-to-itself.
    if (overrideRaw === this.categoriesOverrideKey) return;
    this.categoriesOverrideKey = overrideRaw;

    if (overrideRaw) {
      const lines = overrideRaw.split('\n').map((l) => l.trim()).filter(Boolean);
      const html = lines
        .map((line) => {
          const [label, link, icon] = line.split('|').map((p) => (p || '').trim());
          const iconHTML = icon
            ? `<span class="hero-slider__category-icon"><img src="${icon}" alt="${label}" loading="lazy" width="60" height="60"></span>`
            : '<span class="hero-slider__category-icon"></span>';
          return `<a href="${link || '#'}" class="hero-slider__category">${iconHTML}<span class="hero-slider__category-label">${label}</span></a>`;
        })
        .join('');
      this.categoriesTrack.innerHTML = html;
    } else {
      this.categoriesTrack.innerHTML = this.defaultCategoriesHTML;
    }
  }

  startAutoplay() {
    if (this.slides.length < 2 || !this.autoplayEnabled) return;
    this.stopAutoplay();
    this.timer = setInterval(() => {
      // Desktop hover is already handled by the mouseenter/mouseleave listeners
      // below (they stop/restart the interval itself). This guard covers the
      // gaps they can't: a backgrounded tab, and now also scrolled-out-of-view -
      // touch devices never fire hover at all, so document.hidden and
      // [in-viewport] are the only real protection, matching the pattern
      // already used by announcement-bar.js / slideshow.js.
      if (document.hidden || !this.root.hasAttribute('in-viewport')) return;
      this.goTo(this.current + 1);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-hero-slider]').forEach((root) => {
    new HeroSlider(root);
  });
});
